import React, { useState, useEffect } from "react"
import styled, { css } from "styled-components"
import Cleave from "cleave.js/react"
import loanCalc from "loan-calc"
import { motion, AnimatePresence } from "framer-motion"
import feather from "feather-icons"

import Donut from "./donut"
import Icon from "../global/icon"
import ExternalLinkIcon from "@images/global/external-link.svg"

import ABIModal from "../global/abi-modal-new-window-to-quote"
import ClientSideOnly from "@components/global/client-side-only"

const size = {
  tablet: 950,
  smalltablet: 750,
  mobile: 600,
}
const below = Object.keys(size).reduce((acc, label) => {
  acc[label] = (...args) => css`
    @media (max-width: ${size[label]}px) {
      ${css(...args)};
    }
  `
  return acc
}, {})

const CalcWrapper = styled.div`
  margin: 0 auto;
  max-width: 1248px;
  padding: 96px 48px;
  overflow: hidden;
  ${below.tablet`
  padding: 48px;
  `}

  ${below.smalltablet`
  padding: 48px 24px 0;
  `}
  .help, .text, .circle {
    transition: 0.3s ease-in-out;
  }
  .help {
    position: relative;
    cursor: pointer;
    :hover {
      .text {
        stroke: white;
      }
      .circle {
        fill: var(--pacific-blue);
        stroke: var(--pacific-blue);
      }
    }
  }

  h2 {
    margin-bottom: 8px;
  }

  div {
    &.subtitle {
      font-family: "Source Sans Pro", sans-serif;
      font-size: 22px;
      line-height: 28px;
      font-weight: 600;
      color: var(--grey);
      /* max-width: 500px; */
      margin-bottom: 32px;
      ${below.tablet`
  font-size: 16px;
  line-height: 24px;
  `}
    }
  }

  label {
    font-family: "Source Sans Pro", sans-serif;
    font-weight: 400;
    display: block;
    font-size: 20px;
    line-height: 28px;
    ${below.tablet`
  font-size: 12px;
line-height: 16px;
  `}
  }

  .faux-input {
    padding: 5px 0;
    margin: 0;
    font-size: 20px;
    line-height: 28px;
    width: 124px;
  }

  input {
    color: #75808f;
    border: none;
    outline: none;
    padding: 5px 0;
    margin-bottom: 32px;
    border-bottom: 1px solid #e2e8f0;
    font-size: 36px;
    line-height: 44px;
    font-weight: 400;
    transition: 0.3s ease-in-out;
    width: 100%;
    min-width: 0;
    ${below.tablet`
  font-size: 22px;
line-height: 28px;
  `}
    &:focus {
      color: black;
    }
    &.alt-input {
      background-color: transparent;
      font-size: 20px;
      line-height: 28px;
      color: #4a5568;
      margin: 0;
      display: block;
      max-width: 124px;
      &:focus {
        color: black;
      }
    }
  }
  &.dark-variant {
    input {
      background: transparent;
    }
    *:not(button):not(.outputs *) {
      color: #fff;
    }
    .disclosure {
      background: var(--baltic-blue-hex);
      padding: 0;
    }
    .subtitle {
      color: #fff;
    }
  }
`
const TermButton = styled.button`
  cursor: pointer;
  background-color: #edf2f7;
  color: var(--grey);
  width: 64px;
  height: 40px;
  font-size: 20px;
  line-height: 28px;
  border-radius: 2px;
  border: none;
  margin-right: 24px;
  transition: 0.3s ease-in-out;
  :last-child {
    margin-right: 0;
  }

  &.selected,
  :hover {
    background-color: var(--pacific-blue);
    color: white;
  }
`
const Prefix = styled.div`
  /* border-bottom: 1px solid #e2e8f0; */
  color: #2d3748;
  font-weight: 400;
  font-size: 22px;
  line-height: 28px;
  height: auto;
  text-align: center;
  margin: 0 24px 0 8px;
  ${below.tablet`
  font-size: 16px;
  line-height: 20px;
  `}
  &.alt-prefix {
    min-width: 0;
    height: auto;
    width: auto;
    font-size: 20px;
    line-height: 28px;
    padding: 5px 8px;
  }
  &.faux-prefix {
    min-width: 0;
    height: auto;
    width: auto;
    font-size: 20px;
    line-height: 28px;
    padding: 5px 8px;
    border: none;
  }
`
const LabelRow = styled.div`
  display: flex;
  align-items: ${props => props.alignItems || "center"};
  margin: ${props => props.margin || 0};
  justify-content: ${props => props.justifyContent || "flex-start"};
  width: ${props => props.flexWidth || "initial"};
`
const InputRow = styled(LabelRow)`
  border-bottom: 1px solid #e2e8f0;
  margin-bottom: 32px;
  input {
    border: none;
    margin-bottom: 0;
  }
  align-items: center;
  flex: ${props => props.flex || "initial"};
  &.down-percent {
    margin-left: 24px;
    max-width: 40%;
    ${below.smalltablet`
      max-width: 30%;
     flex: auto;
    `}
  }
`
const DesktopDisclosure = styled.div`
  font-size: 12px;
  line-height: 16px;
  color: var(--grey);
  ${below.smalltablet`
    display: none;
  `}
`
const MobileDisclosure = styled(DesktopDisclosure)`
  display: none;
  background-color: #edf2f7;
  padding: 16px 24px 8px;
  ${below.smalltablet`
    display: block;
  `}
`

const IconWrapper = styled.div`
  position: relative;
  cursor: pointer;
  transition: 0.3s ease-in-out;
  padding: 6px 0px 0px 12px;

  ::before {
    font-size: 12px;
    line-height: 16px;
    text-align: left;
    position: absolute;
    height: auto;
    width: 248px;
    top: -8px;
    left: -60px;
    transform: translateY(-100%);
    background: var(--ice-grey);
    border: 0.5px solid #e2e8f0;
    box-shadow: 0px 5px 30px rgba(117, 128, 143, 0.15);
    border-radius: 2px;
    padding: 12px;
    display: block;
    pointer-events: none;
    opacity: 0;
    transition: 0.3s ease-in-out;
  }
  :hover {
    ::before {
      opacity: 1;
      pointer-events: initial;
    }
  }
  &.price {
    ::before {
      content: "The selling price of the property.";
    }
  }
  &.down-payment {
    ::before {
      content: "The percentage of your home’s purchase price that is paid upfront.";
    }
  }
  &.interest-rate {
    ::before {
      content: "The amount a lender charges for the use of assets expressed as a percentage of the principal.";
    }
  }
  &.loan-term {
    ::before {
      content: "The set period of time that you make payments on your home loan.";
    }
  }
  &.principle-interest {
    ::before {
      content: "Two portions of your monthly payment include the original amount borrowed (principal) and the cost of borrowing (interest).";
    }
  }
  &.property-taxes {
    ::before {
      content: "The tax that you pay as a property owner, levied by the city, county or municipality.";
    }
  }
  &.hoi {
    ::before {
      content: "Homeowners insurance: a standard insurance policy that covers damage to your home and protects your personal property.";
    }
  }
  &.hoa {
    ::before {
      content: "Fees paid to a homeowners association by community residents in exchange for common area maintenance and upkeep. (This only applies to homes within an HOA.)";
    }
  }
  &.pmi {
    ::before {
      content: "Private mortgage insurance: a monthly premium required on most conventional loans with a down payment of less than 20%.";
    }
  }
`

const HOIIconWrapper = styled.div`
  position: relative;
  cursor: pointer;
  transition: 0.3s ease-in-out;
  padding: 6px 0px 0px 12px;
  &:hover .hoi-tool-tip {
    display: block;
  }
`

const HOIToolTip = styled.div`
  display: none;
  text-align: left;
  position: absolute;
  height: auto;
  width: 248px;
  top: 4px;
  left: -60px;
  transform: translateY(-100%);
  background: var(--ice-grey);
  border: 0.5px solid #e2e8f0;
  box-shadow: 0px 5px 30px rgba(117, 128, 143, 0.15);
  border-radius: 2px;
  padding: 12px;
  transition: 0.3s ease-in-out;
  cursor: default;
  p {
    font-size: 12px;
    line-height: 16px;
    margin-bottom: 18px;
    &:last-of-type {
      margin-bottom: 0;
    }
    span {
      color: var(--pacific-blue);
      &:hover {
        cursor: pointer;
      }
    }
  }
`

const Bullet = styled.div`
  pointer-events: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: ${props => props.color || "#0B395B"};
  margin-right: 24px;
  ${below.tablet`
  margin-right: 8px;
  `}
`
const DonutWrapper = styled.div`
  position: relative;
`
const DonutContainer = styled.div`
  position: relative;
  // height: 250px;
  // width: 300px;
  max-width: 100%;
  width: 350px;
  margin: 0 auto 48px;
  ${below.tablet`
  // height: 180px;
  // width: 180px;
  margin: 0 auto 32px;
  `}
`
const CalcContainer = styled.div`
  display: flex;
  align-items: stretch;
  ${below.smalltablet`
  display: block;
  `}
`
const Inputs = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-right: 48px;
  flex: 1;

  .down-payment-input {
    border-bottom: none;
    margin-bottom: 0;
  }

  .no-border {
    border-bottom: none;
  }
  ${below.tablet`
    margin-right: 12px;
  `}
  ${below.smalltablet`
    margin-right: 0;
  `}
`
const OutputRow = styled(LabelRow)`
  justify-content: space-between;
  width: 100%;
  border: 1px solid transparent;
  padding: 12px 8px;
  transition: 0.3s ease-in-out;
  label {
    font-size: 16px;
    line-height: 20px;
  }
  :hover {
    border: 1px solid ${props => props.hoverColor || "#264765"};
    box-shadow: 0px 3px 24px rgba(0, 0, 0, 0.05);
    border-radius: 2px;
  }
  &.no-hover {
    :hover {
      border: 1px solid transparent;
      box-shadow: none;
    }
  }
`
const OutputLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`
const OutputAmount = styled(OutputLabel)`
  max-width: 108px;
`
const Outputs = styled(Inputs)`
  position: relative;
  margin-top: 0px;
  margin-right: 0px;
  margin-left: 48px;
  background-color: var(--ice-grey);
  border-radius: 2px;
  padding: 48px 24px 48px;
  ${below.tablet`
    padding: 32px 12px;
    margin-left: 12px;
  `}

  ${below.smalltablet`
  padding: 32px 0 24px;
  margin-left: 0;
  :after {
    position: absolute;
    content: "";
    background-color: var(--ice-grey);
    width: 100vw;
    height: 100%;
    z-index: -10;
    top: 0;
    left: -24px;
  }
  `}
`
const MonthlyPaymentAmount = styled.div`
  color: var(--grey);
  font-weight: 600;
  font-size: 44px;
  line-height: 60px;
  position: absolute;
  display: block;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`
const MonthlyPaymentLabel = styled.div`
  font-size: 16px;
  line-height: 20px;
  text-align: center;
  color: var(--grey);
`

const ABInsuranceLinkButton = styled.button`
  width: 100%;
  background: none;
  color: inherit;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  outline: inherit;
  margin-top: 48px;
  ${below.tablet`
  margin-top:16px;
  `}
  &:hover {
    text-decoration: none;
  }

  @media (max-width: 750px) {
    display: none;
  }
`
const MobileButtonContainer = styled.div`
  padding: 20px 24px 16px;
  background-color: #edf2f7;
  @media (min-width: 751px) {
    display: none;
  }
`

const MobileHr = styled.hr`
  border-top: 1px solid #e2e8f0;

  border-bottom: unset;
  margin-top: 0px;
  margin-bottom: 0px;
`

const ABInsuranceLinkButtonMobile = styled.button`
  width: 100%;
  background: none;
  color: inherit;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  outline: inherit;
  margin-top: 20px;

  .mobile {
    background: #4a5568;
    p {
      color: white;
    }
    img {
      filter: brightness(0) saturate(100%) invert(74%) sepia(100%) saturate(0%)
        hue-rotate(310deg) brightness(112%) contrast(111%);
    }
    &:hover {
      background: #fff;
      border: 1px solid #4a5568;
      p {
        color: #4a5568;
      }
      img {
        filter: unset;
      }
    }
  }
  &:hover {
    text-decoration: none;
  }
`

const ABInsuranceLinkContainer = styled.div`
  display: flex;
  justify-content: center;
  border: 1px solid var(--grey);
  padding: 12px 40px;
  box-sizing: border-box;
  margin-top: 48px;
  border-radius: 2px;
  ${below.tablet`
  padding:12px 0px;
  `}
  &:last-of-type {
    margin-top: 0;
  }

  &:hover {
    background: var(--grey);
  }
  &:hover p {
    color: white;
  }
  &:hover img {
    filter: brightness(0) saturate(100%) invert(74%) sepia(100%) saturate(0%)
      hue-rotate(310deg) brightness(112%) contrast(111%);
  }

  p {
    color: var(--grey);
    font-weight: 600;
    font-size: 16px;
    line-height: 20px;
    margin-right: 13px;
    margin-bottom: 0px;
    text-decoration: none;
    &:hover {
    }
  }
  img {
    width: 20px;
    height: 20px;
  }
`

const InvisibleButton = styled.button`
  background: none;
  color: inherit;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  outline: inherit;
`

const InputLabelContainer = styled.div`
  label:focus-within {
    background: var(--pacific-blue);
  }
`

const InputFieldsContainer = styled.div``

const Calculator = props => {
  const [showCalculator, setShowCalculator] = useState(false)
  const [pieData, setPieData] = useState([
    {
      id: "pi",
      label: "principal and interest",
      value: 870.41,
      color: "#0B395B",
    },
    {
      id: "insurance",
      label: "homeowners insurance",
      value: 166.67,
      color: "#A682ED",
    },
    {
      id: "taxes",
      label: "property taxes",
      value: 187.5,
      color: "#25B5F3",
    },
    {
      id: "dues",
      label: "hoa dues",
      value: 0,
      color: "#1CC0A5",
    },
    {
      id: "pmi",
      label: "mortgage insurance",
      value: 0,
      color: "#FFD53E",
    },
  ])

  const [isABIModalActive, setIsABIModalActive] = useState(false)

  const [values, setValues] = useState({
    homePrice: 250000,
    downPayment: 50000,
    downPaymentPercent: 20,
    interestRate: 7,
    loanTerm: 360,
    insurance: 135.42,
    tax: 156.25,
    hoa: 0,
    pmi: 0,
  })

  const [monthlyTaxes, setMonthlyTaxes] = useState(156.25)
  const [monthlyInsurance, setMonthlyInsurance] = useState(135.42)
  const [monthlyHOA, setMonthlyHOA] = useState(0)
  const [monthlyPMI, setMonthlyPMI] = useState(0)
  const [monthlyPayment, setMonthlyPayment] = useState(1013)
  const [monthlyTotal, setMonthlyTotal] = useState(1305)
  // const [totalInterest, setTotalInterest] = useState()

  useEffect(() => {
    if (props.pageHash?.match("calculator")) setShowCalculator(true)
  }, [props.pageHash])

  const handleInputChange = event => {
    const { name, value } = event.target
    setValues({
      ...values,
      [name]:
        parseFloat(value.replace(/,/g, "")) > 0
          ? parseFloat(value.replace(/,/g, ""))
          : 0,
    })
  }

  const handleDependentChange = event => {
    const { name, value } = event.target
    if (name === "downPayment") {
      let dollarDown =
        parseFloat(value.replace(/,/g, "")) > 0
          ? parseFloat(value.replace(/,/g, ""))
          : 0

      if (dollarDown > values.homePrice) {
        dollarDown = values.homePrice
      }

      let downPercent =
        (parseFloat(value.replace(/,/g, "")) / values.homePrice) * 100 > 0
          ? (parseFloat(value.replace(/,/g, "")) / values.homePrice) * 100
          : 0
      if (downPercent > 100) {
        downPercent = 100
      }

      setValues({
        ...values,
        downPayment: dollarDown,
        downPaymentPercent: downPercent,
      })
    } else if (name === "downPaymentPercent") {
      let percentDown =
        parseFloat(value.replace(/,/g, "")) > 0
          ? parseFloat(value.replace(/,/g, ""))
          : 0

      if (percentDown > 100) {
        percentDown = 100
      }

      let downDollar =
        parseFloat(value.replace(/,/g, "")) * 0.01 * values.homePrice > 0
          ? parseFloat(value.replace(/,/g, "")) * 0.01 * values.homePrice
          : 0

      if (downDollar > values.homePrice) {
        downDollar = values.homePrice
      }

      setValues({
        ...values,
        downPayment: downDollar,
        downPaymentPercent: percentDown,
      })
    } else if (name === "homePrice") {
      let percentDown =
        (values.downPayment / parseFloat(value.replace(/,/g, ""))) * 100 > 0
          ? (values.downPayment / parseFloat(value.replace(/,/g, ""))) * 100
          : 0
      if (percentDown > 100) {
        percentDown = 100
      }
      setValues({
        ...values,
        homePrice:
          parseFloat(value.replace(/,/g, "")) > 0
            ? parseFloat(value.replace(/,/g, ""))
            : 0,
        downPaymentPercent: percentDown,
      })
    }
  }

  useEffect(() => {
    // LET'S CALCULATE OUR INSURANCE, TAXES, HOA, PMI
    let downPayment = values.downPayment > 0 ? values.downPayment : 0
    let homePrice = values.homePrice > 0 ? values.homePrice : 0
    let loanAmount = homePrice - downPayment > 0 ? homePrice - downPayment : 0
    let insurance = values.insurance
    let taxes = values.tax
    let hoa = values.hoa
    let PMI = values.pmi

    let monthlyPI =
      loanAmount && loanAmount > 0 && values.interestRate > 0
        ? loanCalc.paymentCalc({
            amount: loanAmount,
            rate: values.interestRate,
            termMonths: values.loanTerm,
          })
        : 0

    /*
    setTotalInterest(
      loanAmount > 0 && values.interestRate > 0
        ? loanCalc.totalInterest({
            amount: loanAmount,
            rate: values.interestRate,
            termMonths: values.loanTerm,
          })
        : 0
    )
    */

    setMonthlyInsurance(insurance ? insurance : 0)
    setMonthlyTaxes(taxes ? taxes : 0)
    setMonthlyHOA(hoa ? hoa : 0)
    setMonthlyPMI(PMI ? PMI : 0)
    setMonthlyTotal(
      monthlyPI + monthlyHOA + monthlyInsurance + monthlyTaxes + monthlyPMI,
    )
    setMonthlyPayment(monthlyPI)
    // console.log(totalInterest)

    setPieData([
      {
        id: "pi",
        label: "Principal & interest",
        value: monthlyPayment,
        color: "#0B395B",
      },
      {
        id: "insurance",
        label: "Homeowners insurance",
        value: monthlyInsurance,
        color: "#A682ED",
      },
      {
        id: "taxes",
        label: "Property taxes",
        value: monthlyTaxes,
        color: "#25B5F3",
      },
      {
        id: "dues",
        label: "HOA",
        value: monthlyHOA,
        color: "#1CC0A5",
      },
      {
        id: "pmi",
        label: "Mortgage insurance",
        value: monthlyPMI,
        color: "#FFD53E",
      },
    ])
  }, [
    values.downPayment,
    values.homePrice,
    values.insurance,
    values.tax,
    values.hoa,
    values.pmi,
    values.interestRate,
    values.loanTerm,
    monthlyHOA,
    monthlyPMI,
    monthlyInsurance,
    monthlyTaxes,
    monthlyPayment,
    // totalInterest,
  ]) // eslint-disable-line react-hooks/exhaustive-deps

  /*
  useEffect(() => {
    console.table(values)
    console.log(`PMI required: ${monthlyPMI}`)
    console.log(`monthly P&I: ${monthlyPayment}`)
    console.log(`monthly total: ${monthlyTotal.toFixed(2)}`)
    console.log(`total interest: ${totalInterest}`)
    console.log(`monthly insurance: ${monthlyInsurance.toFixed(2)}`)
    console.log(`monthly taxes: ${monthlyTaxes.toFixed(2)}`)
    console.log(`hoa: ${monthlyHOA.toFixed(2)}`)
  })
  */
  const hasABI =
    props.licensedStates &&
    (props.licensedStates.includes("NC") ||
      props.licensedStates.includes("SC") ||
      props.licensedStates.includes("VA") ||
      props.licensedStates.includes("KY") ||
      props.licensedStates.includes("TN") ||
      props.licensedStates.includes("GA"))

  return (
    <>
      <CalcWrapper
        className={`calculator ${
          props.variant === "dark" ? "dark-variant" : ""
        }`}
      >
        <h2
          className={`flex font-variant-numeric--primary leading-[60px] justify-between text-[46px] -tracking-[.92px] ${
            props.shouldToggleCalculator ? "border-b pb-[15px]" : ""
          } ${
            showCalculator && props.shouldToggleCalculator
              ? "mb-[10px]"
              : "!mb-0"
          }`}
        >
          What can you afford?
          {props.shouldToggleCalculator && (
            <button
              dangerouslySetInnerHTML={{
                __html: feather.icons[
                  showCalculator ? "chevron-up" : "chevron-down"
                ].toSvg({}),
              }}
              onClick={() =>
                setShowCalculator(prevShowCalculator => !prevShowCalculator)
              }
            />
          )}
        </h2>
        <AnimatePresence>
          {(showCalculator || !props.shouldToggleCalculator) && (
            <motion.div
              initial={{
                opacity: 0,
              }}
              animate={{
                opacity: 1,
              }}
              exit={{ opacity: 0 }}
            >
              <div className="subtitle">
                Use my mortgage calculator to estimate your monthly cost.*
              </div>
              <CalcContainer id="calculator">
                <Inputs>
                  <InputFieldsContainer>
                    <InputLabelContainer>
                      <LabelRow>
                        <label className="label-text" htmlFor="homePrice">
                          Home Price
                        </label>
                        <IconWrapper className="price">
                          <Icon icon="help" />
                        </IconWrapper>
                      </LabelRow>

                      <InputRow>
                        <Prefix>$</Prefix>
                        <Cleave
                          options={{
                            numeral: true,
                            numeralThousandsGroupStyle: "thousand",
                          }}
                          maxLength="9"
                          autoComplete="off"
                          type="text"
                          name="homePrice"
                          value={values.homePrice}
                          onChange={handleDependentChange}
                        />
                      </InputRow>
                    </InputLabelContainer>

                    <LabelRow>
                      <label htmlFor="downPayment">Down Payment</label>
                      <IconWrapper className="down-payment">
                        <Icon icon="help" />
                      </IconWrapper>
                    </LabelRow>

                    <InputRow className="down-payment-input">
                      <InputRow flex="auto">
                        <Prefix>$</Prefix>
                        <Cleave
                          options={{
                            numeral: true,
                            numeralThousandsGroupStyle: "thousand",
                          }}
                          id="downPayment"
                          maxLength="9"
                          autoComplete="off"
                          type="text"
                          name="downPayment"
                          value={values.downPayment}
                          onChange={handleDependentChange}
                        />
                      </InputRow>

                      <InputRow className="down-percent">
                        <Prefix>%</Prefix>

                        <Cleave
                          options={{
                            numeral: true,
                            numeralThousandsGroupStyle: "thousand",
                          }}
                          maxLength="5"
                          autoComplete="off"
                          type="text"
                          name="downPaymentPercent"
                          value={values.downPaymentPercent}
                          onChange={handleDependentChange}
                        />
                      </InputRow>
                    </InputRow>

                    <LabelRow>
                      <label htmlFor="interestRate">Interest Rate</label>
                      <IconWrapper className="interest-rate">
                        <Icon icon="help" />
                      </IconWrapper>
                    </LabelRow>

                    <InputRow>
                      <Prefix>%</Prefix>
                      <Cleave
                        options={{
                          numeral: true,
                          numeralThousandsGroupStyle: "thousand",
                          numeralDecimalScale: 3,
                        }}
                        id="interestRate"
                        maxLength="5"
                        autoComplete="off"
                        type="text"
                        name="interestRate"
                        value={values.interestRate}
                        onChange={handleInputChange}
                      />
                    </InputRow>

                    <LabelRow>
                      <label htmlFor="term">Loan term (years)</label>
                      <IconWrapper className="loan-term">
                        <Icon icon="help" />
                      </IconWrapper>
                    </LabelRow>

                    <InputRow className="no-border" margin="8px 0 40px">
                      <TermButton
                        className={
                          values.loanTerm === 360 ? "selected" : undefined
                        }
                        onClick={() => setValues({ ...values, loanTerm: 360 })}
                      >
                        30
                      </TermButton>
                      <TermButton
                        className={
                          values.loanTerm === 240 ? "selected" : undefined
                        }
                        onClick={() => setValues({ ...values, loanTerm: 240 })}
                      >
                        20
                      </TermButton>
                      <TermButton
                        className={
                          values.loanTerm === 180 ? "selected" : undefined
                        }
                        onClick={() => setValues({ ...values, loanTerm: 180 })}
                      >
                        15
                      </TermButton>
                    </InputRow>

                    <DesktopDisclosure className="disclosure">
                      *This calculator is made available to you as an
                      informational tool. All examples are hypothetical and are
                      for illustrative purposes only. Any amount calculated is
                      an estimate only. The actual amount may be higher or lower
                      depending on individual scenarios, credit scores and
                      market conditions. Atlantic Bay Mortgage Group does not
                      guarantee the applicability of the above terms in regards
                      to your individual circumstances. This information is not
                      intended to replace the advice of a tax or financial
                      professional.
                    </DesktopDisclosure>
                    <MobileDisclosure className="disclosure">
                      *This calculator is made available to you as an
                      informational tool. All examples are hypothetical and are
                      for illustrative purposes only. Any amount calculated is
                      an estimate only. The actual amount may be higher or lower
                      depending on individual scenarios, credit scores and
                      market conditions. Atlantic Bay Mortgage Group does not
                      guarantee the applicability of the above terms in regards
                      to your individual circumstances. This information is not
                      intended to replace the advice of a tax or financial
                      professional.
                    </MobileDisclosure>
                    {props.shouldShowABIInsurance && (
                      <button
                        className={`button button--tertiary gap-[10px] group/button mt-[32px] mobile:mb-[58px] w-full ${
                          props.variant === "dark"
                            ? "border-white text-white"
                            : ""
                        }`}
                        onClick={() => setIsABIModalActive(true)}
                      >
                        Get your quote from AB Insurance{" "}
                        <div
                          dangerouslySetInnerHTML={{
                            __html: feather.icons["external-link"].toSvg({
                              class:
                                "group-hover/button:stroke-pacific-blue-1 stroke-white w-[20px] h-[20px]",
                              "stroke-width": 2,
                            }),
                          }}
                        />
                      </button>
                    )}
                  </InputFieldsContainer>
                </Inputs>

                <Outputs className="outputs">
                  <DonutWrapper>
                    <DonutContainer>
                      <ClientSideOnly>
                        <Donut
                          pieData={pieData}
                          monthlyTotal={monthlyTotal
                            .toFixed(2)
                            .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                        />
                      </ClientSideOnly>
                      <MonthlyPaymentAmount>
                        {/* $
                  {monthlyTotal
                    .toFixed(2)
                    .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  <MonthlyPaymentLabel>Monthly Payment</MonthlyPaymentLabel> */}
                      </MonthlyPaymentAmount>
                    </DonutContainer>
                  </DonutWrapper>

                  <OutputRow className="no-hover">
                    <OutputLabel>
                      <Bullet />
                      <label htmlFor="price">Principal & interest</label>
                      <IconWrapper className="principle-interest">
                        <Icon icon="help-white" />
                      </IconWrapper>
                    </OutputLabel>

                    <OutputAmount>
                      <Prefix className="faux-prefix">$</Prefix>
                      <div className="faux-input">
                        {monthlyPayment
                          .toFixed()
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                      </div>
                    </OutputAmount>
                  </OutputRow>

                  <OutputRow hoverColor="#48B9EA">
                    <OutputLabel>
                      <Bullet color="#48B9EA" />
                      <label htmlFor="taxes">Property taxes</label>
                      <IconWrapper className="property-taxes">
                        <Icon icon="help-white" />
                      </IconWrapper>
                    </OutputLabel>

                    <OutputAmount>
                      <Prefix className="alt-prefix">$</Prefix>
                      <Cleave
                        className="alt-input"
                        options={{
                          numeral: true,
                          numeralThousandsGroupStyle: "thousand",
                        }}
                        maxLength="5"
                        autoComplete="off"
                        type="text"
                        name="tax"
                        value={monthlyTaxes
                          .toFixed()
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                        onChange={handleInputChange}
                      />
                    </OutputAmount>
                  </OutputRow>

                  <OutputRow hoverColor="#9F7AEA">
                    <OutputLabel className="hoi">
                      <Bullet color="#9F7AEA" />
                      <label htmlFor="HOI">HOI</label>
                      {hasABI ? (
                        <HOIIconWrapper>
                          <HOIToolTip className="hoi-tool-tip">
                            <p>
                              Homeowners insurance: a standard insurance policy
                              that covers damage to your home and protects your
                              personal property.
                            </p>
                            <p>
                              <InvisibleButton
                                onClick={() => setIsABIModalActive(true)}
                              >
                                <span>Get a quote </span>
                              </InvisibleButton>{" "}
                              from AB Insurance.
                            </p>
                          </HOIToolTip>
                          <Icon icon="help-white" />
                        </HOIIconWrapper>
                      ) : (
                        <IconWrapper className="hoi">
                          <Icon icon="help" />
                        </IconWrapper>
                      )}
                    </OutputLabel>

                    <OutputAmount>
                      <Prefix className="alt-prefix">$</Prefix>
                      <Cleave
                        className="alt-input"
                        options={{
                          numeral: true,
                          numeralThousandsGroupStyle: "thousand",
                        }}
                        maxLength="5"
                        autoComplete="off"
                        type="text"
                        name="insurance"
                        value={monthlyInsurance
                          .toFixed()
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                        onChange={handleInputChange}
                      />
                    </OutputAmount>
                  </OutputRow>

                  <OutputRow hoverColor="#38B2AC">
                    <OutputLabel>
                      <Bullet color="#38B2AC" />
                      <label htmlFor="HOA">HOA Dues</label>
                      <IconWrapper className="hoa">
                        <Icon icon="help-white" />
                      </IconWrapper>
                    </OutputLabel>
                    <OutputAmount>
                      <Prefix className="alt-prefix">$</Prefix>
                      <Cleave
                        className="alt-input"
                        options={{
                          numeral: true,
                          numeralThousandsGroupStyle: "thousand",
                        }}
                        maxLength="5"
                        autoComplete="off"
                        type="text"
                        name="hoa"
                        value={monthlyHOA
                          .toFixed()
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                        onChange={handleInputChange}
                      />
                    </OutputAmount>
                  </OutputRow>

                  <OutputRow hoverColor="#ECC94B">
                    <OutputLabel>
                      <Bullet color="#ECC94B" />
                      <label htmlFor="PMI">PMI</label>
                      <IconWrapper className="pmi">
                        <Icon icon="help-white" />
                      </IconWrapper>
                    </OutputLabel>
                    <OutputAmount>
                      <Prefix className="alt-prefix">$</Prefix>
                      <Cleave
                        className="alt-input"
                        options={{
                          numeral: true,
                          numeralThousandsGroupStyle: "thousand",
                        }}
                        maxLength="5"
                        autoComplete="off"
                        type="text"
                        name="pmi"
                        value={monthlyPMI
                          .toFixed()
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                        onChange={handleInputChange}
                      />
                    </OutputAmount>
                  </OutputRow>
                </Outputs>
              </CalcContainer>
            </motion.div>
          )}
        </AnimatePresence>
      </CalcWrapper>{" "}
      <AnimatePresence>
        {isABIModalActive && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <ABIModal toggleModal={setIsABIModalActive} />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}

Calculator.defaultProps = {
  variant: "light",
}

export default Calculator
